import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronRight, LogIn, LogOut, Crown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo-official.png";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

const programLinks = [
  { title: "My Team", href: "/my-team", description: "Find your child's team info" },
  { title: "League Organizing", href: "/league-organizing", description: "How our league operates" },
  { title: "League Rules", href: "/league-rules", description: "Official flag football rules" },
  { title: "League Standings", href: "/standings", description: "Current team standings" },
  { title: "Schedules", href: "/schedules", description: "Game schedules and important dates" },
  { title: "Tournament Brackets", href: "/tournaments", description: "Live playoff brackets" },
  { title: "Events & Calendar", href: "/events", description: "All league events and calendar sync" },
];


const getInvolvedLinks = [
  { title: "Join Our Team (Apply)", href: "/join", description: "One-click application & background check" },
  { title: "Become a Coach", href: "/get-involved/coach", description: "Coach our young athletes" },
  { title: "Become a Volunteer", href: "/get-involved/volunteer", description: "Help on game day" },
  { title: "Become an Official", href: "/get-involved/official", description: "Referee our games" },
  { title: "Become a Sponsor", href: "/get-involved/sponsor", description: "Support our league" },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  
  const [getInvolvedOpen, setGetInvolvedOpen] = useState(false);
  const location = useLocation();
  const { user, isSuperAdmin, isLoading: authLoading, signOut } = useAuth();
  const { toast } = useToast();

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Signed out",
        description: "You've been logged out successfully.",
      });
    }
  };

  // Handle scroll effect — use rAF to batch layout reads and avoid forced reflow
  useEffect(() => {
    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        setScrolled(window.scrollY > 10);
        rafId = null;
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header 
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-300",
        scrolled 
          ? "bg-background/95 backdrop-blur-md shadow-lg border-b border-border/50" 
          : "bg-background border-b border-border"
      )}
    >
      <div className="container flex h-18 md:h-22 items-center justify-between">
        {/* Logo with hover effect */}
        {/* Mobile: Logo + Styled text */}
        <Link 
          to="/" 
          className="flex items-center gap-2 lg:hidden group"
        >
          <img 
            src={logo} 
            alt="Back to the Roots Flag Football"
            fetchPriority="high"
            className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
            width={56}
            height={56}
          />
          <div className="flex flex-col">
            <span className="font-display text-sm font-bold leading-tight text-foreground">
              Back to The Roots
            </span>
            <span className="font-display text-xs font-semibold text-secondary group-hover:text-secondary/80 transition-colors">
              Flag Football
            </span>
          </div>
        </Link>

        {/* Desktop: Logo image */}
        <Link 
          to="/" 
          className="hidden lg:flex items-center gap-3 group relative"
        >
          <div className="relative overflow-hidden rounded-lg">
            <img 
              src={logo} 
              alt="Back to the Roots Flag Football"
              fetchPriority="high"
              className="h-20 w-auto transition-transform duration-300 group-hover:scale-105"
              width={80}
              height={80}
            />
          </div>
          {/* Decorative underline on hover */}
          <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-secondary to-accent transition-all duration-300 group-hover:w-full" />
        </Link>

        {/* Desktop Navigation */}
        <NavigationMenu className="hidden lg:flex">
          <NavigationMenuList className="gap-1">
            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link 
                  to="/" 
                  className={cn(
                    "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-all duration-200",
                    "hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent focus:outline-none",
                    location.pathname === "/" && "text-accent bg-accent/5"
                  )}
                >
                  Home
                  <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-accent transition-all duration-200 group-hover:w-1/2 group-hover:left-1/4" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link 
                  to="/about" 
                  className={cn(
                    "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-all duration-200",
                    "hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent focus:outline-none",
                    location.pathname === "/about" && "text-accent bg-accent/5"
                  )}
                >
                  About
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuTrigger className="hover:bg-accent/10 hover:text-accent data-[state=open]:bg-accent/10">
                Programs
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[400px] gap-2 p-4 md:w-[500px] md:grid-cols-2 bg-background border border-border rounded-lg shadow-xl">
                  {programLinks.map((link) => (
                    <ListItem key={link.title} title={link.title} href={link.href}>
                      {link.description}
                    </ListItem>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>


            <NavigationMenuItem>
              <NavigationMenuTrigger className="hover:bg-accent/10 hover:text-accent data-[state=open]:bg-accent/10">
                Get Involved
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[400px] gap-2 p-4 md:w-[500px] md:grid-cols-2 bg-background border border-border rounded-lg shadow-xl">
                  {getInvolvedLinks.map((link) => (
                    <ListItem key={link.title} title={link.title} href={link.href}>
                      {link.description}
                    </ListItem>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link 
                  to="/events" 
                  className={cn(
                    "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-all duration-200",
                    "hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent focus:outline-none",
                    location.pathname === "/events" && "text-accent bg-accent/5"
                  )}
                >
                  Events
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link 
                  to="/sponsors" 
                  className={cn(
                    "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-all duration-200",
                    "hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent focus:outline-none",
                    location.pathname === "/sponsors" && "text-accent bg-accent/5"
                  )}
                >
                  Sponsors
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link 
                  to="/gallery" 
                  className={cn(
                    "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-all duration-200",
                    "hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent focus:outline-none",
                    location.pathname === "/gallery" && "text-accent bg-accent/5"
                  )}
                >
                  Gallery
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link 
                  to="/contact" 
                  className={cn(
                    "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-all duration-200",
                    "hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent focus:outline-none",
                    location.pathname === "/contact" && "text-accent bg-accent/5"
                  )}
                >
                  Contact
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        {/* Register Button + Auth */}
        <div className="hidden lg:flex items-center gap-3">
          {/* Super Admin Badge / Auth Button */}
          {authLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : isSuperAdmin ? (
            <div className="flex items-center gap-2">
              <Link 
                to="/admin"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm font-medium hover:bg-accent/20 transition-colors"
              >
                <Crown className="h-3.5 w-3.5" />
                Admin
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="text-muted-foreground hover:text-foreground"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : user ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="text-muted-foreground hover:text-foreground"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Sign Out
            </Button>
          ) : (
            <Link to="/auth">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <LogIn className="h-4 w-4 mr-1" />
                Admin
              </Button>
            </Link>
          )}

          <Button 
            asChild 
            className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
          >
            <Link to="/schedules">View Schedules</Link>
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden p-2 rounded-lg hover:bg-accent/10 transition-colors"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <div className="relative w-6 h-6">
            <span className={cn(
              "absolute left-0 w-6 h-0.5 bg-foreground transition-all duration-300",
              mobileMenuOpen ? "top-3 rotate-45" : "top-1"
            )} />
            <span className={cn(
              "absolute left-0 top-3 w-6 h-0.5 bg-foreground transition-all duration-300",
              mobileMenuOpen ? "opacity-0 scale-0" : "opacity-100 scale-100"
            )} />
            <span className={cn(
              "absolute left-0 w-6 h-0.5 bg-foreground transition-all duration-300",
              mobileMenuOpen ? "top-3 -rotate-45" : "top-5"
            )} />
          </div>
        </button>
      </div>

      {/* Mobile Menu with animation */}
      <div className={cn(
        "lg:hidden overflow-hidden transition-all duration-300 ease-in-out border-t bg-background",
        mobileMenuOpen ? "max-h-[calc(100vh-4rem)] opacity-100" : "max-h-0 opacity-0 border-t-0"
      )}>
        <nav className="container py-4 space-y-2">
          {/* Quick Actions - Register & Admin */}
          <div className="flex gap-3 pb-4 border-b border-border/50 mb-2">
            <Button 
              asChild 
              className={cn(
                "flex-1 font-semibold py-3 text-base",
                location.pathname.startsWith("/schedules")
                  ? "bg-secondary text-secondary-foreground ring-2 ring-secondary ring-offset-2"
                  : "bg-secondary hover:bg-secondary/90 text-secondary-foreground"
              )}
            >
              <Link to="/schedules" onClick={() => setMobileMenuOpen(false)}>
                View Schedules
              </Link>
            </Button>
            
            {authLoading ? (
              <div className="flex items-center justify-center px-5 rounded-md border border-border">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : isSuperAdmin ? (
              <Link 
                to="/admin"
                className={cn(
                  "flex items-center justify-center gap-2 px-5 py-3 rounded-md font-medium text-base",
                  location.pathname.startsWith("/admin")
                    ? "bg-accent text-accent-foreground ring-2 ring-accent ring-offset-2"
                    : "bg-accent/10 border border-accent/20 text-accent"
                )}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Crown className="h-4 w-4" />
                Admin
              </Link>
            ) : (
              <Link 
                to="/auth" 
                onClick={() => setMobileMenuOpen(false)}
              >
                <Button 
                  variant={location.pathname === "/auth" ? "default" : "outline"} 
                  size="lg"
                  className={location.pathname === "/auth" ? "ring-2 ring-primary ring-offset-2" : ""}
                >
                  <LogIn className="h-5 w-5 mr-2" />
                  Admin
                </Button>
              </Link>
            )}
          </div>

          {/* Top-level links in 2-column grid */}
          <div className="grid grid-cols-2 gap-2">
            <MobileNavLink to="/" active={location.pathname === "/"}>
              Home
            </MobileNavLink>
            <MobileNavLink to="/about" active={location.pathname === "/about"}>
              About
            </MobileNavLink>
          </div>
          
          {/* Programs Collapsible */}
          <Collapsible open={programsOpen} onOpenChange={setProgramsOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full py-3 px-3 font-medium text-base text-left hover:text-accent hover:bg-accent/5 rounded-md transition-colors">
              <span>Programs</span>
              <ChevronDown className={cn(
                "h-5 w-5 transition-transform duration-200",
                programsOpen && "rotate-180"
              )} />
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
              <div className="grid grid-cols-2 gap-1.5 pl-3 py-2 border-l-2 border-accent/20 ml-3">
                {programLinks.map((link) => (
                  <Link 
                    key={link.href} 
                    to={link.href} 
                    className={cn(
                      "block py-2 px-3 text-sm rounded-md transition-colors",
                      "hover:bg-accent/10 hover:text-accent",
                      location.pathname === link.href && "bg-accent/10 text-accent"
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.title}
                  </Link>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>


          {/* Get Involved Collapsible */}
          <Collapsible open={getInvolvedOpen} onOpenChange={setGetInvolvedOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full py-3 px-3 font-medium text-base text-left hover:text-accent hover:bg-accent/5 rounded-md transition-colors">
              <span>Get Involved</span>
              <ChevronDown className={cn(
                "h-5 w-5 transition-transform duration-200",
                getInvolvedOpen && "rotate-180"
              )} />
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
              <div className="grid grid-cols-2 gap-1.5 pl-3 py-2 border-l-2 border-accent/20 ml-3">
                {getInvolvedLinks.map((link) => (
                  <Link 
                    key={link.href} 
                    to={link.href} 
                    className={cn(
                      "block py-2 px-3 text-sm rounded-md transition-colors",
                      "hover:bg-accent/10 hover:text-accent",
                      location.pathname === link.href && "bg-accent/10 text-accent"
                    )}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.title}
                  </Link>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Bottom links in 2-column grid */}
          <div className="grid grid-cols-2 gap-2">
            <MobileNavLink to="/events" active={location.pathname === "/events"}>
              Events
            </MobileNavLink>
            <MobileNavLink to="/sponsors" active={location.pathname === "/sponsors"}>
              Sponsors
            </MobileNavLink>
            <MobileNavLink to="/gallery" active={location.pathname === "/gallery"}>
              Gallery
            </MobileNavLink>
            <MobileNavLink to="/contact" active={location.pathname === "/contact"}>
              Contact
            </MobileNavLink>
          </div>

          {/* Mobile Auth - Sign Out only (if signed in) */}
          {(user || isSuperAdmin) && (
            <div className="pt-4 border-t border-border/50">
              <Button
                variant="ghost"
                size="default"
                className="w-full text-muted-foreground"
                onClick={() => {
                  handleSignOut();
                  setMobileMenuOpen(false);
                }}
              >
                <LogOut className="h-5 w-5 mr-2" />
                Sign Out
              </Button>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
};

// Mobile nav link component
const MobileNavLink = ({ 
  to, 
  children, 
  active 
}: { 
  to: string; 
  children: React.ReactNode; 
  active?: boolean;
}) => (
  <Link 
    to={to} 
    className={cn(
      "block py-2.5 px-3 text-base font-medium transition-colors rounded-md",
      "hover:text-accent hover:bg-accent/5",
      active && "text-accent bg-accent/5"
    )}
  >
    {children}
  </Link>
);

const ListItem = ({
  className,
  title,
  children,
  href,
  ...props
}: {
  className?: string;
  title: string;
  children: React.ReactNode;
  href: string;
}) => {
  const location = useLocation();
  const isActive = location.pathname === href;
  
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          to={href}
          className={cn(
            "block select-none space-y-1 rounded-lg p-3 leading-none no-underline outline-none transition-all duration-200",
            "hover:bg-accent/10 hover:text-accent focus:bg-accent/10 focus:text-accent",
            "group border border-transparent hover:border-accent/20",
            isActive && "bg-accent/10 text-accent border-accent/20",
            className
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none flex items-center gap-1">
            {title}
            <ChevronRight className="w-3 h-3 opacity-0 -translate-x-2 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0" />
          </div>
          <p className="line-clamp-2 text-xs leading-snug text-muted-foreground group-hover:text-accent/70">
            {children}
          </p>
        </Link>
      </NavigationMenuLink>
    </li>
  );
};

export default Header;
